import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FileText, CalendarDays, Truck, Receipt, Sparkles, Settings, LogOut, Menu, UserCog, Tablet, Lock, CreditCard, Clock, Star } from "lucide-react";
import { LangSwitch } from "./LangSwitch";
import { Sheet, SheetContent } from "./ui/sheet";
import { Button } from "./ui/button";
import { Logo } from "./Shared";
import { useAuth } from "../context/AuthContext";
import { can, hasFeature, FEATURE_TIER, ROLES } from "../lib/labels";

const NAV = [
  { to: "/app", label: "Tableau de bord", icon: LayoutDashboard, end: true, id: "dashboard", perm: "dashboard" },
  { to: "/app/clients", label: "Clients (CRM)", icon: Users, id: "clients", perm: "clients" },
  { to: "/app/avis", label: "Avis clients", icon: Star, id: "avis", perm: "clients" },
  { to: "/app/devis", label: "Devis & volume", icon: FileText, id: "devis", perm: "quotes" },
  { to: "/app/planning", label: "Planning", icon: CalendarDays, id: "planning", perm: "moves" },
  { to: "/app/flotte", label: "Équipes & véhicules", icon: Truck, id: "flotte", perm: "fleet" },
  { to: "/app/factures", label: "Factures", icon: Receipt, id: "factures", perm: "invoices" },
  { to: "/app/assistant", label: "Assistant IA", icon: Sparkles, id: "assistant", perm: "ai", feature: "ai" },
  { to: "/nomade", label: "Application Nomade", icon: Tablet, id: "nomade", perm: "field", feature: "field" },
  { to: "/app/equipe", label: "Utilisateurs & accès", icon: UserCog, id: "equipe", perm: "team" },
  { to: "/app/parametres", label: "Paramètres", icon: Settings, id: "parametres", perm: "settings" },
  { to: "/app/abonnement", label: "Abonnement", icon: CreditCard, id: "abonnement", perm: "billing" },
];

function PlanBanner() {
  const { user } = useAuth();
  const sub = user?.subscription;
  if (!sub || !["trial", "none"].includes(sub.plan) && sub.status !== "past_due") return null;
  const days = sub.trial_ends_at ? Math.max(0, Math.ceil((new Date(sub.trial_ends_at) - new Date()) / 86400000)) : 0;
  const text = sub.plan === "trial" ? `Essai Premium gratuit : ${days} jour(s) restant(s)` : sub.status === "past_due" ? "Paiement de l'abonnement en échec : mettez à jour votre moyen de paiement" : "Période d'essai terminée : choisissez une formule pour continuer";
  const urgent = sub.plan !== "trial" || days <= 3;
  return (
    <div data-testid="plan-banner" className={`no-print flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm font-medium lg:pl-72 ${urgent ? "bg-amber-100 text-amber-900" : "bg-[#0b2c6b] text-white"}`}>
      <span className="flex items-center gap-2"><Clock className="h-4 w-4" />{text}</span>
      {can(user, "billing") && <Link to="/app/abonnement" data-testid="plan-banner-link" className="rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-[#0b2c6b]">Choisir ma formule</Link>}
    </div>
  );
}

function NavList({ onNavigate }) {
  const { user } = useAuth();
  return (
    <nav className="flex flex-col gap-1">
      {NAV.filter((n) => can(user, n.perm)).map(({ to, label, icon: Icon, end, id, feature }) => (
        <NavLink
          key={to}
          to={hasFeature(user, feature) ? to : "/app/abonnement"}
          end={end}
          onClick={onNavigate}
          data-testid={`nav-${id}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-[#0b2c6b] text-white shadow-sm" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`
          }
        >
          <Icon className="h-4 w-4" />
          {label}
          {!hasFeature(user, feature) ? <span className="ml-auto flex items-center gap-1 text-[10px] font-bold text-slate-400" data-testid={`nav-lock-${id}`}><Lock className="h-3 w-3" />{FEATURE_TIER[feature]}</span>
            : id === "assistant" && <span className="ml-auto rounded bg-teal-400/20 px-1.5 text-[10px] font-bold text-teal-600">IA</span>}
        </NavLink>
      ))}
    </nav>
  );
}

function UserBox() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <p className="truncate text-sm font-semibold text-slate-900" data-testid="sidebar-company-name">{user?.company_name}</p>
      <p className="truncate text-xs text-slate-500">{user?.email}</p>
      <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-teal-600" data-testid="sidebar-user-role">{ROLES[user?.role]}</p>
      <div className="mt-2"><LangSwitch loggedIn /></div>
      <Button
        variant="ghost"
        size="sm"
        data-testid="logout-button"
        className="mt-2 w-full justify-start px-2 text-slate-600"
        onClick={async () => { await logout(); navigate("/login"); }}
      >
        <LogOut className="mr-2 h-4 w-4" /> Déconnexion
      </Button>
    </div>
  );
}

export default function AppLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#f6f8fb]">
      <PlanBanner />
      <aside className="no-print fixed inset-y-0 left-0 hidden w-64 flex-col justify-between border-r border-slate-200 bg-white p-5 lg:flex">
        <div className="space-y-8">
          <Logo to="/app" />
          <NavList />
        </div>
        <UserBox />
      </aside>
      <header className="no-print sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-4 backdrop-blur-lg lg:hidden">
        <Logo to="/app" />
        <Button variant="ghost" size="icon" data-testid="mobile-menu-button" onClick={() => setOpen(true)}>
          <Menu className="h-5 w-5" />
        </Button>
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="flex w-72 flex-col justify-between p-5">
          <div className="space-y-8">
            <Logo to="/app" />
            <NavList onNavigate={() => setOpen(false)} />
          </div>
          <UserBox />
        </SheetContent>
      </Sheet>
      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10 lg:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
